import React from 'react';
import { useTheme } from '../context/ThemeContext';
import TMISLogo from '../img/tmis_logo.png';
import TMISLogoW from '../img/tmis_logo_w.png';

// Decorative, animated sign-in illustration for the blue panel (hidden from
// assistive tech). Animation lives in index.css (.lg-anim-*) and is switched
// off under prefers-reduced-motion.
function LoginIllustration() {
  return (
    <svg className="lg-illus" viewBox="0 0 340 250" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id="lg-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#fff" stopOpacity=".22" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse className="lg-anim-glow" cx="170" cy="130" rx="160" ry="112" fill="url(#lg-glow)" />
      <circle className="lg-anim-twinkle" cx="36" cy="56" r="3" fill="#fff" />
      <circle className="lg-anim-twinkle d2" cx="308" cy="36" r="4" fill="#fff" />
      <circle className="lg-anim-twinkle d3" cx="316" cy="200" r="3" fill="#fff" />
      <circle className="lg-anim-twinkle d4" cx="22" cy="190" r="4" fill="#fff" />

      {/* Monitor */}
      <rect x="82" y="38" width="176" height="124" rx="11" fill="#172554" />
      <rect x="90" y="46" width="160" height="108" rx="6" fill="#fff" />
      <rect x="90" y="46" width="160" height="16" rx="6" fill="#dbeafe" />
      <circle cx="100" cy="54" r="2.6" fill="#1e40af" />
      <circle cx="108" cy="54" r="2.6" fill="#93c5fd" />
      <circle cx="116" cy="54" r="2.6" fill="#93c5fd" />
      <circle cx="170" cy="82" r="13" fill="#dbeafe" />
      <circle cx="170" cy="78" r="5" fill="#1e40af" />
      <path d="M159 91 q11 -9 22 0" fill="#1e40af" />
      <rect x="124" y="104" width="92" height="11" rx="5.5" fill="#dbeafe" />
      <rect x="124" y="120" width="92" height="11" rx="5.5" fill="#dbeafe" />
      {[132, 139, 146, 153].map((cx, i) => (
        <circle key={cx} className={`lg-anim-type t${i + 1}`} cx={cx} cy="125.5" r="1.8" fill="#1e40af" />
      ))}
      <rect className="lg-anim-btn" x="140" y="137" width="60" height="11" rx="5.5" fill="#1e40af" />
      <rect x="156" y="162" width="28" height="12" fill="#172554" />
      <rect x="132" y="174" width="76" height="8" rx="4" fill="#172554" />

      {/* Attendance card */}
      <g transform="translate(14 70)"><g className="lg-anim-float">
        <rect width="84" height="52" rx="10" fill="#fff" />
        <circle cx="20" cy="20" r="10" fill="#d1fae5" />
        <path d="M15 20 l4 4 l7 -8" fill="none" stroke="#065f46" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="36" y="13" width="38" height="6" rx="3" fill="#1e293b" />
        <rect x="36" y="25" width="26" height="5" rx="2.5" fill="#94a3b8" />
        <rect x="12" y="39" width="60" height="5" rx="2.5" fill="#dbeafe" />
        <rect className="lg-anim-fill" x="12" y="39" width="48" height="5" rx="2.5" fill="#1d4ed8" />
      </g></g>

      {/* Results card */}
      <g transform="translate(246 18)"><g className="lg-anim-float d2">
        <rect width="84" height="58" rx="10" fill="#fff" />
        <rect className="lg-anim-bar b1" x="12" y="34" width="9" height="14" rx="2" fill="#93c5fd" />
        <rect className="lg-anim-bar b2" x="26" y="26" width="9" height="22" rx="2" fill="#60a5fa" />
        <rect className="lg-anim-bar b3" x="40" y="18" width="9" height="30" rx="2" fill="#2563eb" />
        <rect className="lg-anim-bar b4" x="54" y="10" width="9" height="38" rx="2" fill="#1e3a8a" />
        <path d="M14 32 L30 24 L44 16 L60 8" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
      </g></g>

      {/* Schedule card */}
      <g transform="translate(20 150)"><g className="lg-anim-float d3">
        <rect width="78" height="58" rx="10" fill="#fff" />
        <rect width="78" height="16" rx="10" fill="#1e40af" />
        <rect y="8" width="78" height="8" fill="#1e40af" />
        <g fill="#cbd5e1">
          <rect x="10" y="24" width="9" height="8" rx="2" /><rect x="25" y="24" width="9" height="8" rx="2" />
          <rect x="40" y="24" width="9" height="8" rx="2" /><rect x="55" y="24" width="9" height="8" rx="2" />
          <rect x="10" y="38" width="9" height="8" rx="2" /><rect x="40" y="38" width="9" height="8" rx="2" />
        </g>
        <rect x="25" y="38" width="9" height="8" rx="2" fill="#1d4ed8" />
        <rect className="lg-anim-blink" x="55" y="38" width="9" height="8" rx="2" fill="#f59e0b" />
      </g></g>

      {/* Padlock */}
      <g transform="translate(236 122)"><g className="lg-anim-lock">
        <rect x="0" y="22" width="56" height="52" rx="10" fill="#fbbf24" />
        <path d="M11 24 v-12 a17 17 0 0 1 34 0 v12" fill="none" stroke="#fde68a" strokeWidth="7" strokeLinecap="round" />
        <circle cx="28" cy="43" r="6" fill="#78350f" />
        <rect x="25.5" y="43" width="5" height="16" rx="2" fill="#78350f" />
      </g></g>

      {/* Success badge */}
      <g transform="translate(222 196)"><g className="lg-anim-pop">
        <circle cx="14" cy="14" r="14" fill="#10b981" />
        <path d="M8 14 l5 5 l9 -10" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </g></g>
    </svg>
  );
}

// Shared split-layout shell for the login and password-recovery pages: form on
// the left, wavy divider + animated illustration on the blue panel, theme toggle
// underneath. Styles live in index.css (.lg-*), all colors ≥ 6:1.
export default function AuthShell({ children, panelTitle = 'Learn. Teach. Manage.', panelText = 'Built for students, trainers and staff.' }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className={`lg-root ${isDark ? 'lg-dark' : ''}`}>
      <div className="lg-shell">
        <div className="lg-form-side">
          <img className="lg-logo" src={isDark ? TMISLogoW : TMISLogo} alt="TMIS" />
          {children}
        </div>

        <div className="lg-panel">
          <svg className="lg-wave" viewBox="0 0 60 420" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <path d="M0 0 H14 C48 70 -6 130 22 200 C50 270 -8 340 20 420 H0 Z" />
          </svg>
          <h2>{panelTitle}</h2>
          <p>{panelText}</p>
          <LoginIllustration />
        </div>
      </div>

      <button type="button" className="lg-theme" onClick={toggle}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}>
        <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'}`} aria-hidden="true" />
        Switch to {isDark ? 'Light' : 'Dark'} Mode
      </button>
    </div>
  );
}
